import { type NextRouter } from 'next/router';

import { queryClient } from '@/utils/api/queryClient';

import { getRegion } from '../i18n/regions';
import { pageRoutes } from '../routes';

interface Token {
  country?: string;
  exp: number;
  iat: number;
  two_factor_authenticated: boolean;
  username: string;
  verification_hash: string;
}

export const tokenKey = 'YO_DK4FA2';
export const refreshTokenKey = 'YO_DKHCQ8';

const rememberMeKey = 'YO_ADMIN_REMEMBER_ME';

export const getStorage = () => (localStorage.getItem(rememberMeKey) ? localStorage : sessionStorage);

export const getToken = () => {
  if (typeof window !== 'undefined') {
    const store = getStorage();
    const region = getRegion();

    return store.getItem(`${tokenKey}_${region?.id.toUpperCase()}`) || null;
  }

  return null;
};

export const getRefreshToken = () => {
  if (typeof window !== 'undefined') {
    const store = getStorage();
    const region = getRegion();

    return store.getItem(`${refreshTokenKey}_${region?.id.toUpperCase()}`) || null;
  }

  return null;
};

export const getRememberMe = () => {
  const store = getStorage();
  return store.getItem(rememberMeKey) || null;
};

export const setToken = (newToken: string) => {
  const store = getStorage();
  const region = getRegion();

  store.setItem(`${tokenKey}_${region?.id.toUpperCase()}`, newToken);
};

export const decodeToken = async (token: string) => {
  if (!token) throw new Error('Token is required');

  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('Invalid token format');

  return JSON.parse(Buffer.from(parts[1], 'base64').toString()) as Token;
};

export const setRememberMe = (rememberMe: boolean) => {
  if (typeof window !== 'undefined') {
    const store = getStorage();
    store.setItem(rememberMeKey, String(rememberMe));
  }
};

export const setRefreshToken = (refreshToken: string) => {
  const store = getStorage();
  const region = getRegion();

  store.setItem(`${refreshTokenKey}_${region?.id.toUpperCase()}`, refreshToken);
};

export const clearTokens = () => {
  const store = getStorage();
  const region = getRegion();

  store.removeItem(rememberMeKey);
  store.removeItem(`${tokenKey}_${region?.id.toUpperCase()}`);
  store.removeItem(`${refreshTokenKey}_${region?.id.toUpperCase()}`);
};

export function logout() {
  clearTokens();
  // Clear all data from queryClient
  queryClient.clear();
}

export const authenticationCompleted = async (router: NextRouter) => {
  const landingPage =
    sessionStorage.getItem('redirectAfterAuthentication') || `/${router.query.countryCode}${pageRoutes.clients}`;

  sessionStorage.removeItem('redirectAfterAuthentication');

  router.push(landingPage);
};

export const redirectIfValidToken = async (
  token: string,
  router: NextRouter,
  {
    cb = undefined,
    disableCallbackIfValidToken = false,
  }: {
    cb?: () => void;
    disableCallbackIfValidToken?: boolean;
  },
) => {
  if (!router.isReady) return;

  const tokenValue = await decodeToken(token);
  const tokenIsForCurrentCountry =
    tokenValue.country?.toLowerCase() === router.query.countryCode && router.query.countryCode != undefined;

  if (tokenIsForCurrentCountry) {
    authenticationCompleted(router);
    if (cb && !disableCallbackIfValidToken) cb();
  } else if (cb) cb();
};
